/**
 * PSP/CRE Tool v3.0 - Global Scenario Toolbar
 *
 * Author: Dr. Tatar Radu MD
 *
 * Version: 1.4.1 (April 7, 2026) - Remove stale TRANSPARENT mode chip branch.
 *   UI-009 removed TRANSPARENT from ModeSelector (3→2 modes) but the Chip color
 *   ternary in the toolbar still had `mode === 'TRANSPARENT' ? 'warning' :`.
 *   Dead code — TRANSPARENT can never be set. Simplified to binary: EXPERT=error, else=success.
 *
 * Version: 1.4.0 (April 6, 2026) - Quick Reference dialog (V4-HELP-001)
 *   ENABLED: Help (?) button — opens Quick Reference dialog (was disabled placeholder).
 *   ADDED: showHelpDialog state + Help dialog with:
 *     - 7-tab workflow table (one row per tab, numbered chips)
 *     - Key outputs chips
 *     - Tips list
 *     - Disabled "Download Full User Manual" button (placeholder — enable when PDF ready)
 *   IMPORTS ADDED: Table, TableBody, TableRow, TableCell, GetApp.
 *
 * Version: 1.3.0 (March 13, 2026) - Toolbar cleanup + split Save button
 *   REMOVED: Settings gear menu — contained only duplicated Load Defaults
 *     (already in Load dialog) and a dead /docs/user-manual link.
 *   REMOVED: Standalone Download IconButton — merged into Save split button.
 *   ADDED: Split Save button (ButtonGroup + Menu):
 *     - Primary click → Save to localStorage only (fast, no file dialog).
 *     - Dropdown arrow → "Save & Download JSON" → localStorage + file download.
 *   ADDED: Help (?) IconButton — disabled placeholder for future User Manual.
 *   KEPT: Load Defaults (ROU/NATO/US) in Load dialog only — correct location.
 *
 * Version: 1.2.0 (November 23, 2025) - FIX: Corrected field names in handleResetConfirm.
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  ButtonGroup,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import {
  Save as SaveIcon,
  FolderOpen as LoadIcon,
  RestartAlt as ResetIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  ArrowDropDown as ArrowDropDownIcon,
  HelpOutline as HelpIcon,
  FolderOpen,
  Warning as WarningIcon,
  GetApp as GetAppIcon,
  MenuBook as MenuBookIcon,
  CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';

import { useTranslation } from 'react-i18next';
import { getDefaultScenario } from './defaultScenarios';
import ScenarioHistory from '../core/ScenarioHistory';
import { lsWrite } from '../utils/lsStore.js';

const ScenarioToolbar = ({ scenario, onScenarioUpdate, onScenarioLoaded, onReset, mode, onCloudSync, onCloudRestore }) => {
  const { t } = useTranslation();

  // State
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog]   = useState(false);
  const [showHelpDialog, setShowHelpDialog]   = useState(false);
  const [saveMessage, setSaveMessage]         = useState(null);

  // Cloud sync status: null | 'syncing' | 'synced' | 'error'
  const [cloudSyncStatus,   setCloudSyncStatus]   = useState(null);
  const [cloudRestoreLoading, setCloudRestoreLoading] = useState(false);
  const cloudSyncTimerRef = useRef(null);

  // Split Save button dropdown anchor
  const saveAnchorRef  = useRef(null);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);

  // Scenario History for Undo/Redo
  const [history] = useState(() => new ScenarioHistory());

  // ============================================================================
  // SAVE TO LOCALSTORAGE (shared by both Save actions)
  // ============================================================================

  const saveToLocalStorage = () => {
    lsWrite('psp_cre_scenario', scenario);
    localStorage.setItem('psp_cre_scenario_timestamp', new Date().toISOString());
    history.push(scenario);
  };

  // ── Cloud sync helper ────────────────────────────────────────────────────
  // Called after every localStorage save. Non-blocking. Updates sync chip.
  const triggerCloudSync = useCallback((scenarioData) => {
    if (!onCloudSync) return;
    if (cloudSyncTimerRef.current) clearTimeout(cloudSyncTimerRef.current);
    setCloudSyncStatus('syncing');
    onCloudSync(scenarioData)
      .then(() => {
        setCloudSyncStatus('synced');
        cloudSyncTimerRef.current = setTimeout(() => setCloudSyncStatus(null), 4000);
      })
      .catch(() => {
        setCloudSyncStatus('error');
        cloudSyncTimerRef.current = setTimeout(() => setCloudSyncStatus(null), 8000);
      });
  }, [onCloudSync]);

  const handleSave = () => {
    try {
      saveToLocalStorage();
      setSaveMessage({ type: 'success', text: t('toolbar.msg_saved') });
      setTimeout(() => setSaveMessage(null), 3000);
      triggerCloudSync(scenario);
    } catch (error) {
      console.error('Error saving scenario:', error);
      setSaveMessage({ type: 'error', text: t('toolbar.msg_save_failed', { error: error.message }) });
      setTimeout(() => setSaveMessage(null), 5000);
    }
  };

  // ============================================================================
  // DOWNLOAD JSON FILE (shared helper)
  // ============================================================================

  const downloadJson = () => {
    const dataStr  = JSON.stringify(scenario, null, 2);
    const blob     = new Blob([dataStr], { type: 'application/json' });
    const url      = URL.createObjectURL(blob);
    const link     = document.createElement('a');
    const timestamp = new Date().toISOString().split('T')[0];
    const echelon   = scenario.force?.echelon          || 'UNKNOWN';
    const standard  = scenario.force?.nationalStandard || 'UNKNOWN';
    link.href     = url;
    link.download = `PSP_CRE_${standard}_${echelon}_${timestamp}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ============================================================================
  // SAVE & DOWNLOAD (split button dropdown action)
  // ============================================================================

  const handleSaveAndDownload = () => {
    setSaveMenuOpen(false);
    // Always download the file regardless of localStorage result.
    // localStorage save is best-effort — quota failures must not block the download.
    downloadJson();
    let storageOk = true;
    try {
      saveToLocalStorage();
      triggerCloudSync(scenario);
    } catch (error) {
      storageOk = false;
      console.warn('Save & Download: localStorage save failed (quota exceeded). File was still downloaded.', error);
    }
    setSaveMessage(storageOk
      ? { type: 'success', text: t('toolbar.msg_saved_downloaded') }
      : { type: 'warning', text: t('toolbar.msg_download_storage_full') }
    );
    setTimeout(() => setSaveMessage(null), 5000);
  };

  // ============================================================================
  // LOAD FROM FILE
  // ============================================================================

  const handleLoadFromFile = () => {
    const input    = document.createElement('input');
    input.type     = 'file';
    input.accept   = '.json';

    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const loadedScenario = JSON.parse(event.target.result);

          if (!loadedScenario.force && !loadedScenario.operation_parameters) {
            throw new Error('Invalid scenario file format');
          }

          onScenarioUpdate(loadedScenario);
          onScenarioLoaded?.(loadedScenario); // BUG-LOAD-001: pass scenario so App can re-derive completedTabs
          history.push(loadedScenario);

          setSaveMessage({ type: 'success', text: t('toolbar.msg_loaded') });
          setTimeout(() => setSaveMessage(null), 3000);
          setShowLoadDialog(false);

        } catch (error) {
          console.error('Error loading scenario:', error);
          setSaveMessage({ type: 'error', text: t('toolbar.msg_load_failed', { error: error.message }) });
          setTimeout(() => setSaveMessage(null), 5000);
        }
      };

      reader.onerror = () => {
        setSaveMessage({ type: 'error', text: t('toolbar.msg_read_failed') });
        setTimeout(() => setSaveMessage(null), 5000);
      };

      reader.readAsText(file);
    };

    input.click();
  };

  // ============================================================================
  // LOAD DEFAULT SCENARIO
  // ============================================================================

  const handleLoadDefault = (standard) => {
    try {
      const defaultScenario = getDefaultScenario(standard);
      onScenarioUpdate(defaultScenario);
      onScenarioLoaded?.();
      history.push(defaultScenario);

      setSaveMessage({ type: 'success', text: t('toolbar.msg_default_loaded', { standard }) });
      setTimeout(() => setSaveMessage(null), 3000);
      setShowLoadDialog(false);

    } catch (error) {
      console.error('Error loading default scenario:', error);
      setSaveMessage({ type: 'error', text: t('toolbar.msg_default_failed', { error: error.message }) });
      setTimeout(() => setSaveMessage(null), 5000);
    }
  };

  // ============================================================================
  // RESTORE FROM CLOUD
  // ============================================================================

  const handleRestoreFromCloud = async () => {
    setCloudRestoreLoading(true);
    try {
      const result = await onCloudRestore();
      if (!result) {
        setSaveMessage({ type: 'warning', text: 'No cloud backup found for your account.' });
        setTimeout(() => setSaveMessage(null), 4000);
        setShowLoadDialog(false);
        return;
      }
      onScenarioUpdate(result.data);
      onScenarioLoaded?.(result.data);
      history.push(result.data);
      const savedAt = new Date(result.saved_at).toLocaleString();
      const label   = result.name ? `"${result.name}"` : 'your scenario';
      setSaveMessage({ type: 'success', text: t('toolbar.msg_cloud_restored', { label, savedAt }) });
      setTimeout(() => setSaveMessage(null), 6000);
      setShowLoadDialog(false);
    } catch (err) {
      setSaveMessage({ type: 'error', text: t('toolbar.msg_cloud_restore_failed', { error: err.message }) });
      setTimeout(() => setSaveMessage(null), 6000);
    } finally {
      setCloudRestoreLoading(false);
    }
  };

  // ============================================================================
  // RESET ALL
  // ============================================================================

  const handleResetConfirm = () => {
    try {
      const emptyScenario = {
        version:             '3.0',
        scenario_type:       'HIERARCHICAL',
        mode:                mode,
        created_at:          new Date().toISOString(),
        modified_at:         new Date().toISOString(),
        author:              'Dr. Tatar Radu MD',
        force:               null,
        operation_parameters:  null,
        medical_configuration: null,
        logistics:             null,
        calculation_results:   null,
        reports_generated:     false
      };

      onScenarioUpdate(emptyScenario);

      localStorage.removeItem('psp_cre_scenario');
      localStorage.removeItem('psp_cre_scenario_timestamp');

      history.clear();

      if (onReset) onReset();

      setShowResetDialog(false);
      setSaveMessage({ type: 'info', text: t('toolbar.msg_reset') });
      setTimeout(() => setSaveMessage(null), 3000);

    } catch (error) {
      console.error('Error resetting scenario:', error);
      setSaveMessage({ type: 'error', text: t('toolbar.msg_reset_failed', { error: error.message }) });
      setTimeout(() => setSaveMessage(null), 5000);
    }
  };

  // ============================================================================
  // UNDO / REDO
  // ============================================================================

  const handleUndo = () => {
    const prev = history.undo();
    if (prev) {
      onScenarioUpdate(prev);
      setSaveMessage({ type: 'info', text: t('toolbar.msg_undo') });
      setTimeout(() => setSaveMessage(null), 2000);
    }
  };

  const handleRedo = () => {
    const next = history.redo();
    if (next) {
      onScenarioUpdate(next);
      setSaveMessage({ type: 'info', text: t('toolbar.msg_redo') });
      setTimeout(() => setSaveMessage(null), 2000);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>

          {/* App Title */}
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            PSP/CRE Tool v3.0
            {mode && (
              <Chip
                label={mode}
                size="small"
                color={mode === 'EXPERT' ? 'error' : 'success'}
                sx={{ ml: 2 }}
              />
            )}
          </Typography>

          {/* Toolbar Buttons */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>

            {/* Undo */}
            <Tooltip title="Undo">
              <span>
                <IconButton onClick={handleUndo} disabled={!history.canUndo()} size="small">
                  <UndoIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* Redo */}
            <Tooltip title="Redo">
              <span>
                <IconButton onClick={handleRedo} disabled={!history.canRedo()} size="small">
                  <RedoIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            {/* Split Save Button
                Primary click  → Save to localStorage only (fast, silent).
                Dropdown arrow → "Save & Download JSON" for permanent backup. */}
            <ButtonGroup variant="outlined" size="small" ref={saveAnchorRef}>
              <Tooltip title={t('toolbar.save_tooltip')}>
                <Button startIcon={<SaveIcon />} onClick={handleSave}>
                  {t('toolbar.save')}
                </Button>
              </Tooltip>
              <Tooltip title={t('toolbar.save_options')}>
                <Button
                  sx={{ px: 0.5, minWidth: 28 }}
                  onClick={() => setSaveMenuOpen(prev => !prev)}
                  aria-label="save options"
                >
                  <ArrowDropDownIcon />
                </Button>
              </Tooltip>
            </ButtonGroup>

            <Menu
              anchorEl={saveAnchorRef.current}
              open={saveMenuOpen}
              onClose={() => setSaveMenuOpen(false)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => { handleSave(); setSaveMenuOpen(false); }}>
                <SaveIcon fontSize="small" sx={{ mr: 1 }} />
                {t('toolbar.save_browser')}
              </MenuItem>
              <MenuItem onClick={handleSaveAndDownload}>
                <SaveIcon fontSize="small" sx={{ mr: 1 }} />
                {t('toolbar.save_download_json')}
              </MenuItem>
            </Menu>

            {/* Cloud sync status chip — visible only when a sync is in progress / just finished */}
            {onCloudSync && cloudSyncStatus && (
              <Chip
                size="small"
                label={
                  cloudSyncStatus === 'syncing' ? t('toolbar.cloud_syncing') :
                  cloudSyncStatus === 'synced'  ? t('toolbar.cloud_saved') :
                  t('toolbar.cloud_failed')
                }
                color={
                  cloudSyncStatus === 'error'  ? 'error' :
                  cloudSyncStatus === 'synced' ? 'success' : 'default'
                }
                variant="outlined"
                sx={{ height: 24, fontSize: '0.7rem' }}
              />
            )}

            {/* Load */}
            <Tooltip title={t('toolbar.load_tooltip')}>
              <Button
                variant="outlined"
                startIcon={<LoadIcon />}
                onClick={() => setShowLoadDialog(true)}
                size="small"
              >
                {t('toolbar.load')}
              </Button>
            </Tooltip>

            {/* Reset */}
            <Tooltip title={t('toolbar.reset_tooltip')}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<ResetIcon />}
                onClick={() => setShowResetDialog(true)}
                size="small"
              >
                {t('toolbar.reset')}
              </Button>
            </Tooltip>

            {/* Help — Quick Reference card + manual download placeholder */}
            <Tooltip title={t('toolbar.help_tooltip')}>
              <IconButton size="small" onClick={() => setShowHelpDialog(true)}>
                <HelpIcon />
              </IconButton>
            </Tooltip>

          </Box>
        </Toolbar>

        {/* Status message banner (success / error / info) */}
        {saveMessage && (
          <Alert
            severity={saveMessage.type}
            sx={{ borderRadius: 0 }}
            onClose={() => setSaveMessage(null)}
          >
            {saveMessage.text}
          </Alert>
        )}
      </AppBar>

      {/* ── LOAD DIALOG ─────────────────────────────────────────────────────── */}
      <Dialog open={showLoadDialog} onClose={() => setShowLoadDialog(false)}>
        <DialogTitle>{t('toolbar.load_dialog_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {t('toolbar.load_dialog_body')}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FolderOpen />}
              onClick={handleLoadFromFile}
              fullWidth
            >
              {t('toolbar.load_from_file')}
            </Button>
            {onCloudRestore && (
              <Button
                variant="outlined"
                color="info"
                startIcon={<CloudDownloadIcon />}
                onClick={handleRestoreFromCloud}
                disabled={cloudRestoreLoading}
                fullWidth
              >
                {cloudRestoreLoading ? t('toolbar.restoring_cloud') : t('toolbar.restore_cloud')}
              </Button>
            )}
            <Divider>{t('toolbar.load_default_divider')}</Divider>
            <Button variant="outlined" onClick={() => handleLoadDefault('ROMANIAN')} fullWidth>
              {t('toolbar.load_romanian')}
            </Button>
            <Button variant="outlined" onClick={() => handleLoadDefault('NATO')} fullWidth>
              {t('toolbar.load_nato')}
            </Button>
            <Button variant="outlined" onClick={() => handleLoadDefault('US_ARMY')} fullWidth>
              {t('toolbar.load_us')}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLoadDialog(false)}>{t('toolbar.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* ── HELP / QUICK REFERENCE DIALOG ───────────────────────────────────── */}
      <Dialog
        open={showHelpDialog}
        onClose={() => setShowHelpDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">{t('toolbar.help_title')}</Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>

          {/* ── Workflow ──────────────────────────────────────────────── */}
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, letterSpacing: 0.5 }}>
            {t('toolbar.workflow_title')}
          </Typography>
          <Table size="small" sx={{ mb: 2.5 }}>
            <TableBody>
              {[
                { n: '1', key: 'tab1' },
                { n: '2', key: 'tab2' },
                { n: '3', key: 'tab3' },
                { n: '4', key: 'tab4' },
                { n: '5', key: 'tab5' },
                { n: '6', key: 'tab6' },
                { n: '7', key: 'tab7' },
              ].map(({ n, key }) => (
                <TableRow key={n} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell sx={{ width: 64, py: 0.75, pr: 1 }}>
                    <Chip label={`Tab ${n}`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
                  </TableCell>
                  <TableCell sx={{ width: 96, py: 0.75, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    {t(`toolbar.workflow.${key}_label`)}
                  </TableCell>
                  <TableCell sx={{ py: 0.75, color: 'text.secondary', fontSize: '0.85rem' }}>
                    {t(`toolbar.workflow.${key}_desc`)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* ── Key outputs ───────────────────────────────────────────── */}
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, letterSpacing: 0.5 }}>
            {t('toolbar.outputs_title')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
            {(t('toolbar.outputs', { returnObjects: true }) || []).map((o, i) => (
              <Chip key={i} label={o} size="small" variant="outlined" color="success" />
            ))}
          </Box>

          {/* ── Tips ──────────────────────────────────────────────────── */}
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.75, letterSpacing: 0.5 }}>
            {t('toolbar.tips_title')}
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
            {(t('toolbar.tips', { returnObjects: true }) || []).map((tip, i) => (
              <Box component="li" key={i} sx={{ mb: 0.5 }}>
                <Typography variant="body2">{tip}</Typography>
              </Box>
            ))}
          </Box>

        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 2.5, py: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<GetAppIcon />}
            endIcon={<MenuBookIcon />}
            component="a"
            href="/PSP_CRE_User_Manual.docx"
            download="PSP_CRE_User_Manual.docx"
          >
            {t('toolbar.download_manual')}
          </Button>
          <Button
            variant="contained"
            onClick={() => setShowHelpDialog(false)}
          >
            {t('toolbar.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── RESET CONFIRMATION DIALOG ────────────────────────────────────────── */}
      <Dialog open={showResetDialog} onClose={() => setShowResetDialog(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            {t('toolbar.reset_dialog_title')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {t('toolbar.reset_dialog_body')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('toolbar.reset_will_clear')}
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 2 }}>
            <li>{t('toolbar.reset_force')}</li>
            <li>{t('toolbar.reset_ops')}</li>
            <li>{t('toolbar.reset_medical')}</li>
            <li>{t('toolbar.reset_logistics')}</li>
            <li>{t('toolbar.reset_results')}</li>
            <li>{t('toolbar.reset_storage')}</li>
          </Box>
          <Alert severity="warning">
            {t('toolbar.reset_warning')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResetDialog(false)}>{t('toolbar.cancel')}</Button>
          <Button onClick={handleResetConfirm} color="error" variant="contained" autoFocus>
            {t('toolbar.reset_confirm_btn')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScenarioToolbar;
